import * as React from 'react'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'

interface CurrencyInputProps extends Omit<React.ComponentProps<typeof Input>, 'value' | 'onChange' | 'type'> {
  /** Value in dollars (not cents) */
  value: number | undefined
  /** Called with value in dollars */
  onChange: (value: number) => void
  /** Whether to show the $ prefix */
  showPrefix?: boolean
}

/**
 * CurrencyInput - A controlled input for dollar amounts
 *
 * Key behavior:
 * - Lets users type freely without reformatting mid-keystroke
 * - Formats to 2 decimal places on blur (e.g., "5" → "5.00")
 * - Stores value as dollars (not cents)
 */
export function CurrencyInput({
  value,
  onChange,
  showPrefix = true,
  className,
  ...props
}: CurrencyInputProps) {
  const [displayValue, setDisplayValue] = React.useState(
    value ? value.toFixed(2) : ''
  )
  const [isFocused, setIsFocused] = React.useState(false)

  // Sync display value when external value changes (e.g., form reset)
  // but only when not focused to avoid reformatting while typing
  React.useEffect(() => {
    if (!isFocused) {
      setDisplayValue(value ? value.toFixed(2) : '')
    }
  }, [value, isFocused])

  return (
    <div className="relative">
      {showPrefix && (
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
          $
        </span>
      )}
      <Input
        type="number"
        step="0.01"
        min="0"
        value={displayValue}
        onChange={(e) => {
          setDisplayValue(e.target.value)
          const numValue = e.target.value === '' ? 0 : parseFloat(e.target.value)
          onChange(numValue)
        }}
        onFocus={() => setIsFocused(true)}
        onBlur={() => {
          setIsFocused(false)
          const numValue = parseFloat(displayValue) || 0
          setDisplayValue(numValue.toFixed(2))
        }}
        placeholder="0.00"
        className={cn(showPrefix && 'pl-8', className)}
        {...props}
      />
    </div>
  )
}
